
import { defineComponent, PropType } from "@vue/runtime-core";
import VuePdfEmbed from "vue-pdf-embed";
import { downloadFileNode } from "@/api/course_tree";
import { arraybufferToBase64 } from "@/utils";

const DEFAULT_WIDTH = 780;
const MIN_WIDTH = 400;
const MAX_WIDTH = 1600;
const ZOOM_STEP = 100;

export default defineComponent({
	name: "InlinePublicPdfViewer",
	components: { VuePdfEmbed },
	props: {
		url: {
			type: String,
			required: true as const,
		},
		filename: {
			type: String,
			required: true as const,
		},
		downloadingFile: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["download"],
	async mounted() {
		document.addEventListener("fullscreenchange", this.onFullscreenChange);
		document.addEventListener("webkitfullscreenchange", this.onFullscreenChange);
		document.addEventListener("keydown", this.onKeydown);
		window.addEventListener("resize", this.onWindowResize);
		const buffer = await downloadFileNode(this.url, (e: any) => {
			if (e.total) {
				this.downloadProgress = Math.round((e.loaded / e.total) * 100);
			}
		});
		this.base64Data = arraybufferToBase64(buffer);
		this.downloading = false;
		this.isRendering = true;
	},
	beforeUnmount() {
		document.removeEventListener("fullscreenchange", this.onFullscreenChange);
		document.removeEventListener("webkitfullscreenchange", this.onFullscreenChange);
		document.removeEventListener("keydown", this.onKeydown);
		window.removeEventListener("resize", this.onWindowResize);
	},
	data() {
		return {
			base64Data: "",
			downloading: true,
			downloadProgress: 0,
			isRendering: false,
			currentPage: 1,
			pageCount: 0,
			pageInput: 1,
			width: DEFAULT_WIDTH,
			isFullscreen: false,
			// width to restore to when leaving fullscreen
			savedWidth: DEFAULT_WIDTH,
		};
	},
	computed: {
		base64Source(): string {
			return this.base64Data ? `data:application/pdf;base64,${this.base64Data}` : "";
		},
		atMinZoom(): boolean {
			return this.width <= MIN_WIDTH;
		},
		atMaxZoom(): boolean {
			return this.width >= MAX_WIDTH;
		},
		zoomLabel(): string {
			return `${Math.round((this.width / DEFAULT_WIDTH) * 100)}%`;
		},
	},
	methods: {
		toggleFullscreen() {
			const el = this.$refs.rootEl as any;
			const doc = document as any;
			const current = doc.fullscreenElement || doc.webkitFullscreenElement;
			if (current) {
				(doc.exitFullscreen || doc.webkitExitFullscreen)?.call(doc);
			} else if (el) {
				(el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
			}
		},
		onFullscreenChange() {
			const doc = document as any;
			const current = doc.fullscreenElement || doc.webkitFullscreenElement;
			const nowFullscreen = current === (this.$refs.rootEl as any);
			if (nowFullscreen && !this.isFullscreen) {
				// entering: remember the inline width, then scale to fit the screen
				this.savedWidth = this.width;
				this.isFullscreen = true;
				this.$nextTick(() =>
					requestAnimationFrame(() => requestAnimationFrame(() => this.fitToScreen())),
				);
			} else if (!nowFullscreen && this.isFullscreen) {
				// leaving: restore the previous inline width
				this.isFullscreen = false;
				this.setWidth(this.savedWidth);
			} else {
				this.isFullscreen = nowFullscreen;
			}
		},
		// Scale the page so it fills the fullscreen area while keeping its ratio.
		fitToScreen() {
			if (!this.isFullscreen) return;
			const area = this.$refs.renderArea as HTMLElement | undefined;
			const pageEl = (this.$refs.pdfRef as any)?.$el as HTMLElement | undefined;
			if (!area || !pageEl || !pageEl.offsetWidth) return;
			// aspect ratio is independent of the current render width
			const ratio = pageEl.offsetHeight / pageEl.offsetWidth;
			if (!ratio || !isFinite(ratio)) return;
			const availW = area.clientWidth - 24; // small horizontal breathing room
			const availH = area.clientHeight - 48; // py-6 (top + bottom)
			if (availW <= 0 || availH <= 0) return;
			let targetW = availW;
			if (targetW * ratio > availH) targetW = availH / ratio;
			this.setWidth(Math.max(MIN_WIDTH, Math.floor(targetW)));
		},
		setWidth(newWidth: number) {
			if (newWidth !== this.width) {
				this.isRendering = true;
				this.width = newWidth;
			}
		},
		onWindowResize() {
			if (this.isFullscreen) this.fitToScreen();
		},
		onKeydown(e: KeyboardEvent) {
			// arrow-key page navigation while in fullscreen
			if (!this.isFullscreen) return;
			if (e.key === "ArrowRight" || e.key === "ArrowDown") {
				e.preventDefault();
				this.nextPage();
			} else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
				e.preventDefault();
				this.prevPage();
			}
		},
		handleRender() {
			this.isRendering = false;
			this.pageCount = (this.$refs.pdfRef as any)?.pageCount ?? 0;
		},
		prevPage() {
			if (this.currentPage > 1) {
				this.isRendering = true;
				this.currentPage--;
				this.pageInput = this.currentPage;
			}
		},
		nextPage() {
			if (this.currentPage < this.pageCount) {
				this.isRendering = true;
				this.currentPage++;
				this.pageInput = this.currentPage;
			}
		},
		jumpToPage() {
			const target = Math.min(Math.max(1, this.pageInput ?? 1), this.pageCount || 1);
			this.pageInput = target;
			if (target !== this.currentPage) {
				this.isRendering = true;
				this.currentPage = target;
			}
		},
		zoomIn() {
			if (this.width < MAX_WIDTH) {
				this.isRendering = true;
				this.width = Math.min(MAX_WIDTH, this.width + ZOOM_STEP);
			}
		},
		zoomOut() {
			if (this.width > MIN_WIDTH) {
				this.isRendering = true;
				this.width = Math.max(MIN_WIDTH, this.width - ZOOM_STEP);
			}
		},
	},
});
